import React, { useContext, useEffect, useMemo, useState } from "react";
import { Oval } from "react-loader-spinner";
import axios from "axios";
import AuthContext from "../Context/AuthContext";
import { useHistory } from "react-router-dom";

export default function Login() {
  const history = useHistory();
  const { setLoggedIn, getLoggedIn, setUserData } = useContext(AuthContext);

  const [mail, setMail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [touched, setTouched] = useState({ mail: false, password: false });

  useEffect(() => {
    getLoggedIn(); // oturum bilgisini tazele
  }, [getLoggedIn]);

  const emailError = useMemo(() => {
    if (!touched.mail) return "";
    if (!mail.trim()) return "Email is required.";
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail.trim());
    return ok ? "" : "Please enter a valid email address.";
  }, [mail, touched.mail]);

  const passwordError = useMemo(() => {
    if (!touched.password) return "";
    if (!password) return "Password is required.";
    return "";
  }, [password, touched.password]);

  const formHasError = !!(emailError || passwordError || !mail || !password);

  async function signin(e) {
    e.preventDefault();
    setServerError("");

    // dokunulmuş say ve kontrol et
    setTouched({ mail: true, password: true });
    if (formHasError) return;

    try {
      setLoading(true);
      const res = await axios.post("https://act-api.vercel.app/Login", {
        mail,
        password,
      });

      localStorage.setItem("auth-token", res.data.token);
      setUserData({
        token: res.data.token,
        user: res.data.user,
      });
      setLoggedIn(true);
      history.push("/guidelines");
    } catch (err) {
      const msg =
        err?.response?.data?.hata ||
        err?.message ||
        "Sign in failed. Please try again.";
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[calc(100vh-64px)] grid place-items-center bg-gradient-to-b from-slate-50 to-slate-100 px-4">
      <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        {/* Form Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <div className="mb-6">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Sign in
            </span>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Join our tool
            </h1>
          </div>

          {serverError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {serverError}
            </div>
          )}

          <form onSubmit={signin} className="space-y-4">
            {/* Email */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                type="email"
                autoComplete="email"
                placeholder="name@email.com"
                value={mail}
                onChange={(e) => setMail(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, mail: true }))}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 ${
                  emailError
                    ? "border-red-300 focus:border-red-400"
                    : "border-slate-300 focus:border-blue-400"
                }`}
              />
              {emailError && (
                <p className="mt-1 text-xs text-red-600">{emailError}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Password
              </label>
              <div
                className={`flex items-stretch rounded-lg border ${
                  passwordError
                    ? "border-red-300 focus-within:border-red-400"
                    : "border-slate-300 focus-within:border-blue-400"
                } focus-within:ring-4 focus-within:ring-blue-100`}
              >
                <input
                  type={showPass ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                  className="w-full rounded-l-lg px-3 py-2 text-sm outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="px-3 text-sm text-slate-600 hover:text-slate-900 rounded-r-lg"
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  {showPass ? "Hide" : "Show"}
                </button>
              </div>
              {passwordError && (
                <p className="mt-1 text-xs text-red-600">{passwordError}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || formHasError}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
            >
              {loading ? (
                <Oval height={22} width={22} color="#ffffff" ariaLabel="loading" />
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>

        {/* Illustration */}
        <div className="hidden md:block">
          <img
            src="/Login.gif"
            alt="Login Illustration"
            className="w-full h-auto rounded-2xl shadow-sm select-none"
            draggable="false"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      </div>
    </div>
  );
}
